import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import './ChantingArea.css';

const mantraLabelMap = {
  'Om Namaha Shivaya': 'omNamahaShivaya',
  'Om Namah Shivaya': 'omNamahShivaya',
  'Jai Shree Ram': 'jaiShreeRam',
  'Jai Shree Krishna': 'jaiShreeKrishna',
  'Shree Radha': 'shreeRadha',
  'Sita Ram': 'sitaRam',
  'Om Gan Ganapataye Namah': 'omGanGanapatayeNamah',
  'Om Rudraya Namah': 'omRudrayaNamah',
  'Radhe Krishna': 'radheKrishna',
  'Om Sai Ram': 'omSaiRam',
  'Om Kalabhiravay Namah': 'omKalabhiravayNamah',
  Shreem: 'shreem',
  Aim: 'aim',
  'Hare Krishna Hare Krishna\nKrishna Krishna Hare Hare\nHare Rama Hare Rama\nRama Rama Hare Hare': 'hareKrishnaMahaMantra',
  // Backward compatibility for older saved entries.
  'Hare Krishna': 'hareKrishna',
  'Shree Ram': 'shreeRam',
  'Shree Krishna': 'shreeKrishna',
  'Jai Maa Durga': 'jaiMaaDurga',
  'Maa Durga': 'maaDurga',
  'Jai Hanuman': 'jaiHanuman',
  Hanuman: 'hanuman',
};

const ChantingArea = ({ onChant, divineName, currentBead, totalBeads = 108 }) => {
  const { t } = useLanguage();
  const translatedDivineName = t(
    `bhaktiJaapaPage.divineNames.${mantraLabelMap[divineName]}`,
    {},
    divineName
  );

  // --- SVG Circular Progress Logic ---
  const radius = 85;
  const circumference = 2 * Math.PI * radius;
  const progress = currentBead / totalBeads;
  const strokeDashoffset = circumference * (1 - progress);

  // --- RIPPLE EFFECT LOGIC ---
  const handleChantClick = (event) => {
    // 1. Call the parent onChant function
    onChant();

    // 2. Create the ripple element
    const chantingCircle = event.currentTarget.querySelector('.chanting-circle');
    const ripple = document.createElement('span');
    ripple.classList.add('ripple');
    
    // 3. Calculate position and size
    const rect = chantingCircle.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;
    
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;
    
    // 4. Add to the DOM and remove after animation
    chantingCircle.appendChild(ripple);
    setTimeout(() => {
      ripple.remove();
    }, 600); // Must match the animation duration in CSS
  };

  return (
    <motion.div
      className="chanting-area"
      onClick={handleChantClick}
      whileTap={{ scale: 0.98 }}
    >
      {/* The SVG for the visual mala progress */}
      <svg className="mala-progress-ring" viewBox="0 0 200 200">
        {/* Background track */}
        <circle
          className="mala-progress-ring__track"
          r={radius}
          cx="100"
          cy="100"
        />
        {/* Progress indicator */}
        <motion.circle
          className="mala-progress-ring__indicator"
          r={radius}
          cx="100"
          cy="100"
          style={{ strokeDasharray: circumference, strokeDashoffset }}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 0.5, ease: 'linear' }}
        />
      </svg>
      
      {/* The central clickable circle */}
      <div className="chanting-circle">
        <div className="chanting-text">
          <p>{t('bhaktiJaapaPage.chantingArea.tapToChant')}</p>
          <h3>{translatedDivineName}</h3>
        </div>
      </div>
    </motion.div>
  );
};

export default ChantingArea;
